// src/pages/menu/AgaFemale.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaNotesMedical, FaCheckCircle, FaInfoCircle, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface AgaFemalePageProps {
  ipAllowed: boolean;
}

type NoticeDoc = { title?: string; html?: string };

/** 予約側と揃える：注意事項の文面を更新したらここを更新（キャッシュバスター） */
const NOTICE_VERSION = 'v2025-10-01';

/** public ベースパス（CRA / Vite / 直配信 いずれでも動く） */
function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/** zh 系言語コード（zh, zh-CN など）をすべて zh として扱う */
function normalizeLang(input: string): 'ja' | 'zh' {
  return String(input || '').toLowerCase().startsWith('zh') ? 'zh' : 'ja';
}

const AgaFemalePage: React.FC<AgaFemalePageProps> = ({ ipAllowed }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = normalizeLang(i18n.language);
  const navigate = useNavigate();

  // 薬品価格（DB優先、フォールバックは静的データ）
  const { medicines } = useMedicinePrices('aga_female', lang);

  // 注意事項モーダル
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);

  // 注意事項 JSON
  const [doc, setDoc] = useState<NoticeDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  const treatments = (t('aga_female.treatment_items', { returnObjects: true }) as string[]) || [];
  const recommends = (t('aga_female.recommend_items', { returnObjects: true }) as string[]) || [];

  type PriceItem = { name: string; dosage: string; price: number; taxPrice: number };

  // 固定テキスト（日/中文）
  const L: Record<'ja' | 'zh', {
    sectionTitle: string;
    p1: string;
    p2: string;
    badges: readonly string[];
    seeFaqPrefix: string;
    seeFaqLink: string;
    seeFaqSuffix: string;
    priceTitle: string;
    priceItems: readonly PriceItem[];
    priceUnit: string;
    faqBtn: string;
    noticeBtn: string;
    modalAgree: string;
    modalCta: string;
    modalClose: string;
    cta: string;
  }> = {
    ja: {
      sectionTitle: 'AGA・女性',
      p1: '女性の薄毛（FAGA）は、ホルモンバランス、毛包の感受性、加齢、生活習慣などが複合して起こると考えられています。当院ではオンライン診療にもとづき、スピロノラクトン、ミノキシジル（内服・外用）を中心に、必要に応じて栄養サポート等を組み合わせて継続しやすい治療計画をご提案します。',
      p2: '通院時間 0 分で自宅から相談・継続が可能です。副作用や妊娠計画など、個々のライフステージに合わせて安全性を第一に設計します。',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療（保険適用外）'],
      seeFaqPrefix: 'FAGAに関する詳しいご質問は',
      seeFaqLink: 'FAQ',
      seeFaqSuffix: 'をご覧ください。',
      priceTitle: '価格（診察料 0円）：',
      priceItems: [
        { name: 'スピロノラクトン', dosage: '1か月分', price: 3000, taxPrice: 3300 },
        { name: 'ミノキシジル', dosage: '1か月分', price: 5000, taxPrice: 5500 },
        { name: 'サプリメント (サポート)', dosage: '1か月分', price: 2000, taxPrice: 2200 },
      ],
      priceUnit: '円～',
      faqBtn: 'FAQを見る',
      noticeBtn: '注意事項はこちらから',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
      cta: 'オンライン診療を予約する',
    },
    zh: {
      sectionTitle: 'AGA・女性',
      p1: '女性型脱发（FAGA）常与激素变化、毛囊敏感性、年龄及生活方式等多因素相关。本院基于线上问诊，为您提供以螺内酯、米诺地尔（口服/外用）为主的个体化方案，必要时联合营养支持，便于持续坚持。',
      p2: '在家即可就诊与复诊。我们会结合副作用风险与孕育计划，优先考虑安全性并据此制定治疗策略。',
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗（不适用医保）'],
      seeFaqPrefix: '更多常见问题请参阅',
      seeFaqLink: 'FAQ',
      seeFaqSuffix: '。',
      priceTitle: '价格（诊察费 0 日元）：',
      priceItems: [
        { name: '螺内酯 (Spironolactone)', dosage: '1 个月', price: 3000, taxPrice: 3300 },
        { name: '米诺地尔 (Minoxidil)', dosage: '1 个月', price: 5000, taxPrice: 5500 },
        { name: '焕发系列 (Pantogar)', dosage: '1 个月', price: 2000, taxPrice: 2200 },
      ],
      priceUnit: '日元起',
      faqBtn: '查看 FAQ',
      noticeBtn: '查看注意事项',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
      cta: '立即预约在线咨询',
    },
  } as const;

  /** 注意事項モーダルを開くときに JSON を取得（JP→なければZHにフォールバック／逆も同様） */
  const openNotice = async () => {
    setAgree(false);
    setNoticeOpen(true);
    try {
      setDocLoading(true);

      const base = getPublicBase();
      const primary = `${base}/data/notices/aga_female.${lang}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;
      const fallback = `${base}/data/notices/aga_female.${lang === 'ja' ? 'zh' : 'ja'}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;

      const tryFetch = async (url: string) => {
        const res = await fetch(url, { cache: 'no-cache' });
        return res.ok ? ((await res.json()) as NoticeDoc) : null;
      };

      let json = await tryFetch(primary);
      if (!json) {
        console.warn('[AgaFemalePage] notice primary not found:', primary);
        json = await tryFetch(fallback);
        if (!json) {
          console.warn('[AgaFemalePage] notice fallback not found:', fallback);
          setDoc({ title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
          return;
        }
      }
      setDoc(json);
    } catch (e) {
      console.error('[AgaFemalePage] notice load error:', e);
      setDoc({ title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
    } finally {
      setDocLoading(false);
    }
  };

  // Esc でモーダルを閉じる & 表示中はスクロール抑止
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNoticeOpen(false); };
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  /** 同意後に予約へ遷移（科目プリセット＋同意済み＋入口属性を state で渡す） */
  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    goReserve(navigate, ipAllowed, {
      preselectService: 'aga_female',
      preAgreed: true,
      from: 'service',
    });
  };

  // 価格表は DB(admin設定)優先で描画。API が空(未登録)ならハードコード L[lang].priceItems にフォールバック。
  const dbPriceItems = toSinglePriceItems(medicines, lang);
  const priceItems: any[] = dbPriceItems.length > 0 ? dbPriceItems : [...L[lang].priceItems];

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{t('services.aga_female.title')} - Melodis Clinic</title>
        <meta name="description" content={t('seo.aga_female.description')} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* 見出し */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaNotesMedical className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-rose-400 mr-2">│</span>{t('services.aga_female.title')}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.aga_female.summary')}
          </p>

          {/* 説明（短文） */}
          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8">
            {t('aga_female.description')}
          </p>

          {/* 詳細ブロック */}
          <section className="rounded-2xl border border-rose-100 bg-rose-50/50 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-rose-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-rose-700">{L[lang].sectionTitle}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-rose-200 bg-white px-4 py-3 text-slate-800 text-center"
                >
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed mb-8">
              <p>{L[lang].p1}</p>
              <p>{L[lang].p2}</p>
              <p>
                {L[lang].seeFaqPrefix}
                <button
                  type="button"
                  onClick={() => navigate('/faq/aga-female')}
                  className="underline decoration-rose-300 underline-offset-2 hover:text-rose-700 mx-1"
                >
                  {L[lang].seeFaqLink}
                </button>
                {L[lang].seeFaqSuffix}
              </p>
            </div>

            {/* 価格・リンク */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">{L[lang].priceTitle}</h3>
              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-rose-700 mb-3 border-b border-rose-100 pb-1">
                    {lang === 'ja' ? '主成分薬' : '核心治疗药物'}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {priceItems.slice(0, 2).map((item: any, i: number) => (
                      <div key={i} className="rounded-xl bg-rose-50/30 p-4 border border-rose-100 flex flex-col hover:border-rose-200 transition">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-bold text-slate-800">{item.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 mb-3">{item.dosage}</div>
                        <div className="mt-auto text-right">
                          <div className="flex items-baseline justify-end gap-1">
                            <span className="font-bold text-lg text-slate-900">{item.price.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">{L[lang].priceUnit}</span>
                          </div>
                          <div className="text-[11px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()}{L[lang].priceUnit})
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-rose-700 mb-3 border-b border-rose-100 pb-1">
                    {lang === 'ja' ? 'その他サポート' : '其他辅助支持'}
                  </h4>
                  <ul className="space-y-3 text-slate-700">
                    {priceItems.slice(2).map((item: any, i: number) => (
                      <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1">
                        <span className="flex flex-col text-left">
                          <span className="font-bold text-slate-800">{item.name}</span>
                          <span className="text-[11px] text-slate-500">{item.dosage}</span>
                        </span>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1 justify-end">
                            <span className="font-bold text-slate-900">{item.price.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">{L[lang].priceUnit}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()}{L[lang].priceUnit})
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/faq/aga-female')}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
                >
                  {L[lang].faqBtn} <FaArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={openNotice}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
                >
                  {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>

          {/* 治療項目（タグ） */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 border border-rose-100 bg-rose-50/60">
            <h2 className="text-2xl font-semibold text-rose-700 mb-4">
              {t('aga_female.treatment_title')}
            </h2>
            <div className="flex flex-wrap gap-3">
              {treatments.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm text-slate-800"
                >
                  <FaCheckCircle className="text-rose-500 w-4 h-4" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* おすすめ対象 */}
          <div className="rounded-2xl p-6 md:p-8 bg-slate-50 border border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">
              {t('aga_female.recommend_title')}
            </h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {recommends.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-700"
                >
                  <FaCheckCircle className="text-rose-500 w-5 h-5 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* 予約 CTA（→ モーダル経由） */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <button
                onClick={openNotice}
                className="inline-block bg-rose-500 text-white px-10 py-3.5 text-base md:text-lg rounded-full font-semibold shadow-md hover:bg-rose-600 transition active:scale-95"
              >
                {L[lang].cta}
              </button>
              <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-rose-300/60" />
            </div>
          )}
        </div>
      </div>

      {/* 注意事項モーダル（public/data から読み込み） */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          {/* オーバーレイ */}
          <div className="fixed inset-0 bg-black/40" />
          {/* コンテンツ */}
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {doc?.title || (lang === 'ja' ? '注意事項' : '注意事项')}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
                {docLoading ? (
                  <p className="text-slate-500">{lang === 'ja' ? '読み込み中…' : '加载中…'}</p>
                ) : doc?.html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                ) : (
                  <p className="text-slate-500">
                    {lang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
                  </p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-rose-500 hover:bg-rose-600 active:bg-rose-700' : 'bg-rose-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgaFemalePage;
